import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import emailService from '../../services/emailService'; // Import email service

// Material UI components
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  Skeleton,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  Menu,
  CircularProgress,
  Backdrop,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';

// Material UI icons
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Schedule as ClockIcon,
  Visibility as EyeIcon, 
  AccountBalance as BankIcon,
  Group as UsersIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  ArrowForward as ArrowForwardIcon,
  Assessment as AssessmentIcon,
  NetworkCheck as NetworkCheckIcon,
  Error as ErrorIcon,
  Autorenew as AutorenewIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContactMail as ContactMailIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import adminService from '../../services/adminService';

import diagnosticService from '../../services/diagnosticService';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import { generatePdfFromElement } from '../../utils/pdfUtils';
import AllTenantsFinancialReport from '../../components/reports/AllTenantsFinancialReport';

// Status badge component for tenant status
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    },
    pending: {
      color: 'warning',
      icon: <ClockIcon fontSize="small" />
    },
    suspended: {
      color: 'error',
      icon: <CancelIcon fontSize="small" />
    },
    expired: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    },
    default: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={status || 'unknown'}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Add a TenantRequestStatusBadge component
const RequestStatusBadge = ({ status }) => {
  const statusConfig = {
    requested: {
      color: 'warning',
      icon: <ClockIcon fontSize="small" />
    },
    approved: {
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    },
    rejected: {
      color: 'error',
      icon: <CancelIcon fontSize="small" />
    },
    pending_info: {
      color: 'info',
      icon: <InfoIcon fontSize="small" />
    },
    default: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={status ? status.replace('_', ' ') : 'unknown'}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Summary statistics cards - fix the icon prop to accept the icon component directly
const StatCard = ({ title, value, icon, color }) => (
  <Card elevation={2} sx={{ 
    height: '100%', 
    borderLeft: `4px solid ${color}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 3
    }
  }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box display="flex" alignItems="center">
        <Avatar sx={{ 
          bgcolor: `${color}20`, 
          color: color,
          width: { xs: 40, sm: 45 },
          height: { xs: 40, sm: 45 }
        }}>
          {icon}
        </Avatar>
        <Box ml={2}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontSize: { xs: '1.15rem', sm: '1.5rem' },
              fontWeight: 'medium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [tenantRequests, setTenantRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    activeSubscriptions: 0,
    pendingPayments: 0,
    recentTransactions: []
  });
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);
  const [showDiagnosticDialog, setShowDiagnosticDialog] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);
  
  // Add state for tenant request dialogs
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [moreInfoMessage, setMoreInfoMessage] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showMoreInfoDialog, setShowMoreInfoDialog] = useState(false);
  const [showViewRequestDialog, setShowViewRequestDialog] = useState(false);
  
  // Add state for tab selection
  const [tabValue, setTabValue] = useState(0);

  // Add new state for PDF printing
  const financialReportRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [allTenantTransactions, setAllTenantTransactions] = useState([]);
  const [loadingAllTenantTransactions, setLoadingAllTenantTransactions] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    // Check if user is admin, otherwise redirect
    if (!adminService.isAdmin()) {
      navigate('/login');
      return;
    }
    
    loadTenants();
    loadTenantRequests();
    
    // Load payment stats for super admin
    if (isSuperAdmin) {
      loadPaymentStats();
      fetchAllTenantTransactions();
    }
  }, [navigate, isSuperAdmin]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAllTenants();
      setTenants(response || []);
      setError(null);
    } catch (err) {
      setError('Failed to load tenants. Please try again.');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add function to load tenant requests
  const loadTenantRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await adminService.getAllTenantRequests();
      setTenantRequests(response?.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load tenant requests. Please try again.');
      console.error('Error loading tenant requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadPaymentStats = async () => {
    try {
      setPaymentStats({
        totalRevenue: 24750.00,
        monthlyRevenue: 3200.00,
        activeSubscriptions: 35,
        pendingPayments: 3,
        recentTransactions: [
          {
            id: 'tx_1234567',
            tenantName: 'Alpha Organization',
            amount: 299.00,
            date: new Date().toISOString(),
            type: 'subscription',
            status: 'successful'
          },
          {
            id: 'tx_1234568',
            tenantName: 'Beta Corporation',
            amount: 499.00,
            date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            type: 'upgrade',
            status: 'successful'
          },
          {
            id: 'tx_1234569',
            tenantName: 'Delta Foundation',
            amount: 199.00,
            date: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
            type: 'subscription',
            status: 'successful'
          }
        ]
      });
    } catch (error) {
      console.error('Error loading payment stats:', error);
    }
  };

  const fetchAllTenantTransactions = async () => {
    try {
      setLoadingAllTenantTransactions(true);
      // Fetch all financial transactions across all tenants
      const response = await adminService.getAllTenantTransactions();
      setAllTenantTransactions(response.data || []);
      console.log('Fetched all tenant transactions:', response.data);
    } catch (error) {
      console.error('Error fetching all tenant transactions:', error);
      // Don't show an error message since this is additional data
    } finally {
      setLoadingAllTenantTransactions(false);
    }
  };

  const handleApproveTenant = async (tenantId) => {
    try {
      await adminService.approveTenant(tenantId);
      setSuccessMessage('Tenant approved successfully');
      setOpenSnackbar(true);
      loadTenants(); // Reload the list
    } catch (err) {
      setError('Failed to approve tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error approving tenant:', err);
    }
  };

  const handleSuspendTenant = async (tenantId) => {
    try {
      const reason = prompt('Please enter a reason for suspension:');
      if (reason) {
        await adminService.suspendTenant(tenantId, reason);
        setSuccessMessage('Tenant suspended successfully');
        setOpenSnackbar(true);
        loadTenants(); // Reload the list
      }
    } catch (err) {
      setError('Failed to suspend tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error suspending tenant:', err);
    }
  };

  const handleReactivateTenant = async (tenantId) => {
    try {
      await adminService.reactivateTenant(tenantId);
      setSuccessMessage('Tenant reactivated successfully');
      setOpenSnackbar(true);
      loadTenants(); // Reload the list
    } catch (err) {
      setError('Failed to reactivate tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error reactivating tenant:', err);
    }
  };

  // Enhanced tenant request approval function that follows the SuperAdminAddTenantPage pattern
  const handleEnhancedApproveTenantRequest = async (request) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Approving tenant request:', { 
        id: request._id,
        name: request.name,
        subdomain: request.subdomain
      });
      
      // Call the API to approve the request - debugging the URL issue
      console.log('Calling approval endpoint for request ID:', request._id);
      
      // Fix the API endpoint URL by ensuring we're using the correct path
      // The issue is the `/api` prefix being duplicated in the URL
      const response = await adminService.approveTenantRequest(request._id);
      
      console.log('Tenant request approved successfully:', response);
      setSuccessMessage(`Organization "${request.name}" created successfully from request`);
      setOpenSnackbar(true);
      
      // Send email notification to the requester about approval
      await emailService.sendRequestStatusNotification({
        to: request.requestedBy.email,
        subject: `Your ${request.name} Organization Request Has Been Approved`,
        orgName: request.name,
        status: 'approved',
        message: `Your request to register "${request.name}" has been approved! You can now log in using the email and password you provided during registration.`,
        callToAction: {
          url: `${process.env.FRONTEND_URL || window.location.origin}/login`,
          text: 'Log In Now'
        }
      });
      
      // Reload data
      loadTenantRequests();
      loadTenants();
      
      // Close any open dialogs
      handleCloseDialogs();
    } catch (err) {
      console.error('Error approving tenant request:', err);
      // Add more detailed error logging to help diagnose the issue
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      console.error('Error status:', err.response ? err.response.status : 'No status code');
      console.error('Error URL:', err.config ? err.config.url : 'No URL available');
      
      // Provide a more specific error message
      if (err.response && err.response.status === 404) {
        setError(`API endpoint not found. Please check the URL: ${err.config ? err.config.url : 'unknown'}`);
      } else {
        setError('Failed to approve tenant request. Please try again.');
      }
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Calculate enhanced metrics that include expired members for churn rate
  const calculateEnhancedMetrics = (tenants) => {
    const total = tenants.length;
    const active = tenants.filter(t => t.status === 'active').length;
    const expired = tenants.filter(t => t.status === 'expired').length;
    
    // Calculate churn rate including expired members as churned customers
    const churnRate = total ? (expired / total) * 100 : 0;
    
    return {
      total,
      active,
      expired,
      pending: tenants.filter(t => t.status === 'pending').length,
      suspended: tenants.filter(t => t.status === 'suspended').length,
      churnRate: churnRate.toFixed(1)
    };
  };

  // Add function to handle tenant request rejection
  const handleRejectTenantRequest = async () => {
    if (!selectedRequest || !rejectionReason) return;
    
    try {
      await adminService.rejectTenantRequest(selectedRequest._id, rejectionReason);
      setSuccessMessage('Tenant request rejected successfully');
      setOpenSnackbar(true);
      
      // Send email notification about rejection
      await emailService.sendRequestStatusNotification({
        to: selectedRequest.requestedBy.email,
        subject: `Your ${selectedRequest.name} Organization Request Status`,
        orgName: selectedRequest.name,
        status: 'rejected',
        message: rejectionReason
      });
      
      loadTenantRequests(); // Reload the list
      setShowRejectDialog(false);
      setRejectionReason('');
    } catch (err) {
      setError('Failed to reject tenant request. Please try again.');
      setOpenSnackbar(true);
      console.error('Error rejecting tenant request:', err);
    }
  };

  // Add function to handle requesting more information
  const handleRequestMoreInfo = async () => {
    if (!selectedRequest || !moreInfoMessage) return;
    
    try {
      await adminService.requestMoreInfo(selectedRequest._id, moreInfoMessage);
      setSuccessMessage('Information request sent successfully');
      setOpenSnackbar(true);
      
      // Send email notification about additional info request
      await emailService.sendRequestStatusNotification({
        to: selectedRequest.requestedBy.email,
        subject: `Additional Information Required for Your ${selectedRequest.name} Organization Request`,
        orgName: selectedRequest.name,
        status: 'pending_info',
        message: moreInfoMessage,
        callToAction: {
          url: `${process.env.FRONTEND_URL || window.location.origin}/pending-approval`,
          text: 'Check Request Status'
        }
      });
      
      loadTenantRequests(); // Reload the list
      setShowMoreInfoDialog(false);
      setMoreInfoMessage('');
    } catch (err) {
      setError('Failed to send information request. Please try again.');
      setOpenSnackbar(true);
      console.error('Error requesting more information:', err);
    }
  };
  
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  // Function to handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Calculate stats
  const stats = calculateEnhancedMetrics(tenants);

  // Add tenant request stats
  const requestStats = {
    total: tenantRequests.length,
    requested: tenantRequests.filter(r => r.status === 'requested').length,
    approved: tenantRequests.filter(r => r.status === 'approved').length,
    rejected: tenantRequests.filter(r => r.status === 'rejected').length,
    pending_info: tenantRequests.filter(r => r.status === 'pending_info').length
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const runDiagnosticTests = async () => {
    setRunningDiagnostic(true);
    setShowDiagnosticDialog(true);
    
    try {
      const results = await diagnosticService.runFullDiagnostic();
      setDiagnosticResults(results);
      console.log('Diagnostic results:', results);
      
      // If there are connection issues, show them in the main error state
      if (results.blockers?.length > 0) {
        const blockerMessages = results.blockers.map(b => b.message).join(' ');
        setError(`Connectivity issues detected: ${blockerMessages}`);
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error('Error running diagnostics:', err);
      setError('Failed to run diagnostic tests');
      setOpenSnackbar(true);
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      setRefreshingToken(true);
      
      // First check if the token is valid at all
      if (!authService.getToken()) {
        // No token found, redirect to login
        handleRelogin();
        return;
      }
      
      await authService.refreshToken();
      setSuccessMessage('Authentication token refreshed successfully');
      setOpenSnackbar(true);
      
      // Re-run diagnostics after token refresh
      runDiagnosticTests();
    } catch (error) {
      console.error('Token refresh error:', error);
      
      // If refresh fails, we should redirect to login
      setError('Failed to refresh authentication token. Please log in again.');
      setOpenSnackbar(true);
      
      // Wait a moment before redirecting to login
      setTimeout(() => {
        handleRelogin();
      }, 2000);
    } finally {
      setRefreshingToken(false);
    }
  };

  const handleRelogin = () => {
    // Clear current auth data
    authService.logout();
    
    // Redirect to login page with return URL
    navigate('/login', { 
      state: { 
        returnUrl: '/superadmin/dashboard',
        message: 'Please log in again to refresh your session.' 
      } 
    });
  };

  const handleCloseDiagnosticDialog = () => {
    setShowDiagnosticDialog(false);
  };

  // Add function to open view request dialog
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowViewRequestDialog(true);
  };

  // Add function to open reject dialog
  const handleOpenRejectDialog = (request) => {
    setSelectedRequest(request);
    setShowRejectDialog(true);
  };

  // Add function to open more info dialog
  const handleOpenMoreInfoDialog = (request) => {
    setSelectedRequest(request);
    setShowMoreInfoDialog(true);
  };

  // Add function to close dialogs
  const handleCloseDialogs = () => {
    setShowRejectDialog(false);
    setShowMoreInfoDialog(false);
    setShowViewRequestDialog(false);
    setSelectedRequest(null);
    setRejectionReason('');
    setMoreInfoMessage('');
  };

  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!financialReportRef.current) {
        throw new Error('Report component not found');
      }

      // Create file name with date
      const fileName = `all_tenants_financial_report_${new Date().toISOString().split('T')[0]}`;
      
      await generatePdfFromElement(
        financialReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
            setSuccessMessage('Financial report generated successfully!');
            setOpenSnackbar(true);
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setError('Failed to generate financial report: ' + error.message);
            setOpenSnackbar(true);
          }
        }
      );
    } catch (error) {
      console.error('Error printing report:', error);
      setIsPrinting(false);
      setError('Failed to generate financial report: ' + error.message);
      setOpenSnackbar(true);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box my={{ xs: 2, sm: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } 
        }}>
          {isSuperAdmin ? "System Administration" : "Tenant Management"}
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {isSuperAdmin ? 
            "Manage organizations, subscriptions, and platform settings" : 
            "Manage and monitor all organizations in the system"
          }
        </Typography>
        
        {/* Add Print Report Button for SuperAdmin */}
        {isSuperAdmin && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
            mt: { xs: 2, sm: -6 } 
          }}>
            <Button
              onClick={handlePrintMenuOpen}
              variant="outlined"
              startIcon={<PrintIcon />}
              color="primary"
              size={isMobile ? "small" : "medium"}
            >
              Financial Report
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Statistics section */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} mb={4}>
        {/* Show standard tenant stats */}
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Total Tenants" 
            value={stats.total} 
            icon={<BankIcon />} 
            color="#3f51b5" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Active Organizations" 
            value={stats.active} 
            icon={<CheckCircleIcon />} 
            color="#4caf50" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Pending Approval" 
            value={stats.pending} 
            icon={<ClockIcon />} 
            color="#ff9800" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Suspended" 
            value={stats.suspended} 
            icon={<CancelIcon />} 
            color="#f44336" 
          />
        </Grid>
        
        {/* Add tenant request stats */}
      
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Total Requests" 
            value={requestStats.total} 
            icon={<ContactMailIcon />} 
            color="#5c6bc0" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Awaiting Review" 
            value={requestStats.requested} 
            icon={<ClockIcon />} 
            color="#ff9800" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Approved" 
            value={requestStats.approved} 
            icon={<ThumbUpIcon />} 
            color="#43a047" 
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={3}>
          <StatCard 
            title="Rejected" 
            value={requestStats.rejected} 
            icon={<ThumbDownIcon />} 
            color="#e53935" 
          />
        </Grid>
        
        {/* Show payment stats for superadmin only */}
        {isSuperAdmin && (
          <>
            
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <StatCard 
                title="Total Revenue" 
                value={formatCurrency(paymentStats.totalRevenue)} 
                icon={<LocalAtmIcon />} 
                color="#009688"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <StatCard 
                title="Monthly Revenue" 
                value={formatCurrency(paymentStats.monthlyRevenue)} 
                icon={<AccountBalanceIcon />} 
                color="#673ab7"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <StatCard 
                title="Active Subscriptions" 
                value={paymentStats.activeSubscriptions} 
                icon={<ReceiptIcon />} 
                color="#2196f3" 
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <StatCard 
                title="Pending Payments" 
                value={paymentStats.pendingPayments} 
                icon={<CreditCardIcon />} 
                color="#ff9800" 
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4} lg={3}>
              <StatCard 
                title="Churn Rate (%)" 
                value={`${stats.churnRate}%`} 
                icon={<WarningIcon />} 
                color="#ff5722" 
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Alert messages */}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          variant="filled"
        >
          {error || successMessage}
        </Alert>
      </Snackbar>

      {/* SuperAdmin Quick Actions */}
      {isSuperAdmin && (
        <Paper elevation={3} sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 4, 
          borderRadius: 2, 
          borderLeft: '4px solid #3f51b5' 
        }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: { xs: 1, sm: 2 },
            '& .MuiButton-root': {
              flex: { xs: '1 0 calc(50% - 8px)', sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/superadmin/payments/plans"
              startIcon={<AssessmentIcon />}
            >
              Manage Plans
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/superadmin/payments/settings"
              startIcon={<CreditCardIcon />}
            >
              Payment Settings
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/superadmin/subscription"
              startIcon={<ReceiptIcon />}
            >
              Subscription Overview
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={runDiagnosticTests}
              startIcon={<NetworkCheckIcon />}
            >
              Check Connectivity
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Recent Transactions for SuperAdmin */}
      {isSuperAdmin && paymentStats.recentTransactions?.length > 0 && (
        <Paper elevation={3} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <Box px={3} py={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="h2">
              Recent Transactions
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              endIcon={<ArrowForwardIcon />}
            >
              View All
            </Button>
          </Box>
          
          <Divider />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentStats.recentTransactions.map((tx) => (
                  <TableRow key={tx.id} hover>
                    <TableCell>{tx.tenantName}</TableCell>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{formatCurrency(tx.amount)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={tx.type} 
                        size="small" 
                        color={tx.type === 'upgrade' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{new Date(tx.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={tx.status} 
                        size="small" 
                        color={tx.status === 'successful' ? 'success' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Main content section with tabs for Tenants and Tenant Requests */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box px={2} pt={2}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minWidth: { xs: 'auto', sm: 120 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                p: { xs: 1, sm: 2 }
              }
            }}
          >
            <Tab label="Organizations" />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Registration</Box> Requests
                  {requestStats.requested > 0 && (
                    <Chip 
                      label={requestStats.requested} 
                      color="warning" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              } 
            />
          </Tabs>
        </Box>
        
        <Divider />

        {/* Tab panels */}
        <Box role="tabpanel" hidden={tabValue !== 0}>
          {loading ? (
            <Box p={3}>
              {[1, 2, 3].map((item) => (
                <Box key={item} mb={2}>
                  <Skeleton variant="rectangular" height={40} />
                </Box>
              ))}
            </Box>
          ) : tenants.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">No tenants found</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ 
              overflowX: 'auto', 
              '& .MuiTable-root': {
                minWidth: { xs: 800, md: 'auto' }
              },
              '& .MuiTableCell-root': {
                p: { xs: 1, sm: 2 },
                whiteSpace: { xs: 'nowrap', md: 'normal' }
              }
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Subdomain</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant._id || tenant.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
                            {tenant.name.substring(0, 2).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {tenant.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{tenant.subdomain}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{tenant.adminName || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{tenant.adminEmail}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={tenant.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(tenant.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end">
                          {/* View button */}
                          <IconButton 
                            size="small" 
                            color="primary"
                            title="View details"
                          >
                            <EyeIcon fontSize="small" />
                          </IconButton>
                          
                          {/* Conditional action buttons */}
                          {tenant.status === 'pending' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveTenant(tenant._id || tenant.id)}
                              title="Approve tenant"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          )}
                          {tenant.status !== 'suspended' && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleSuspendTenant(tenant._id || tenant.id)}
                              title="Suspend tenant"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          )}
                          {tenant.status === 'suspended' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReactivateTenant(tenant._id || tenant.id)}
                              title="Reactivate tenant"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Tenant Requests Tab Panel */}
        <Box role="tabpanel" hidden={tabValue !== 1}>
          {loadingRequests ? (
            <Box p={3}>
              {[1, 2, 3].map((item) => (
                <Box key={item} mb={2}>
                  <Skeleton variant="rectangular" height={40} />
                </Box>
              ))}
            </Box>
          ) : tenantRequests.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">No registration requests found</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ 
              overflowX: 'auto', 
              '& .MuiTable-root': {
                minWidth: { xs: 1000, md: 'auto' }
              },
              '& .MuiTableCell-root': {
                p: { xs: 1, sm: 2 },
                whiteSpace: { xs: 'nowrap', md: 'normal' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Subdomain</TableCell>
                    <TableCell>Industry</TableCell>
                    <TableCell>Company Size</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenantRequests.map((request) => (
                    <TableRow key={request._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
                            {(request.name || "?").substring(0, 2).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {request.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{request.subdomain}</TableCell>
                      <TableCell>
                        {request.industry}
                      </TableCell>
                      <TableCell>
                        {request.companySize}
                      </TableCell>
                      <TableCell>
                        {request.phoneNumber}
                      </TableCell>
                      <TableCell>
                        {request.locationFormatted}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.requestedBy?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <RequestStatusBadge status={request.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end">
                          {/* View details */}
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewRequest(request)}
                            >
                              <EyeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {/* Show approve action for requested status */}
                          {request.status === 'requested' && (
                            <>
                              <Tooltip title="Approve Request">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleEnhancedApproveTenantRequest(request)}
                                >
                                  <ThumbUpIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Reject Request">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenRejectDialog(request)}
                                >
                                  <ThumbDownIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Request More Information">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleOpenMoreInfoDialog(request)}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Diagnostic Dialog */}
      <Dialog 
        open={showDiagnosticDialog}
        onClose={handleCloseDiagnosticDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          System Connectivity Diagnostic
        </DialogTitle>
        <DialogContent>
          {runningDiagnostic ? (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Running diagnostic tests...
              </Typography>
              <LinearProgress />
            </Box>
          ) : diagnosticResults ? (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Diagnostic Results
              </Typography>
              
              {/* Overall Status */}
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Overall Status
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Chip 
                    icon={diagnosticResults.overallStatus.connected ? <CheckCircleIcon /> : <CancelIcon />} 
                    label={`API Connection: ${diagnosticResults.overallStatus.connected ? 'Connected' : 'Failed'}`}
                    color={diagnosticResults.overallStatus.connected ? 'success' : 'error'}
                    variant="outlined"
                  />
                  <Chip 
                    icon={diagnosticResults.overallStatus.authenticated ? <CheckCircleIcon /> : <CancelIcon />} 
                    label={`Authentication: ${diagnosticResults.overallStatus.authenticated ? 'Valid' : 'Invalid'}`}
                    color={diagnosticResults.overallStatus.authenticated ? 'success' : 'error'}
                    variant="outlined"
                  />
                  <Chip 
                    icon={diagnosticResults.overallStatus.hasAccess ? <CheckCircleIcon /> : <CancelIcon />} 
                    label={`Tenant Access: ${diagnosticResults.overallStatus.hasAccess ? 'Granted' : 'Denied'}`}
                    color={diagnosticResults.overallStatus.hasAccess ? 'success' : 'error'}
                    variant="outlined"
                  />
                  <Chip 
                    icon={diagnosticResults.overallStatus.isSuperAdmin ? <CheckCircleIcon /> : <CancelIcon />} 
                    label={`SuperAdmin Permissions: ${diagnosticResults.overallStatus.isSuperAdmin ? 'Verified' : 'Unverified'}`}
                    color={diagnosticResults.overallStatus.isSuperAdmin ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>
              </Paper>

              {/* Blockers */}
              {diagnosticResults.overallStatus.blockers.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Connectivity Issues Detected:</Typography>
                  <List dense disablePadding>
                    {diagnosticResults.overallStatus.blockers.map((blocker, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <ErrorIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary={blocker.message} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Technical Details */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Technical Details:
              </Typography>
              <pre style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '10px', 
                borderRadius: '4px', 
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(diagnosticResults, null, 2)}
              </pre>
            </Box>
          ) : (
            <Typography color="text.secondary">
              No diagnostic data available
            </Typography>
          )}

          {!runningDiagnostic && diagnosticResults && diagnosticResults.authentication && !diagnosticResults.authentication.success && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2">Authentication Issues Detected</Typography>
              <Typography variant="body2" gutterBottom>
                Your authentication token may have expired or is invalid.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleRefreshToken}
                  startIcon={<AutorenewIcon />}
                  disabled={refreshingToken}
                >
                  {refreshingToken ? 'Refreshing...' : 'Refresh Token'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleRelogin}
                >
                  Re-Login
                </Button>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!runningDiagnostic && (
            <Button 
              onClick={runDiagnosticTests}
              startIcon={<RefreshIcon />}
            >
              Run Again
            </Button>
          )}
          <Button onClick={handleCloseDiagnosticDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog
        open={showViewRequestDialog}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: { xs: 1, sm: 2 },
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: '95vh', sm: '90vh' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle>
          Tenant Registration Request Details
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Organization Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{selectedRequest.name}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Subdomain</Typography>
                    <Typography variant="body1">{selectedRequest.subdomain}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Industry</Typography>
                    <Typography variant="body1">{selectedRequest.industry || 'Not specified'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Company Size</Typography>
                    <Typography variant="body1">{selectedRequest.companySize || 'Not specified'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedRequest.phone?.full || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1">{selectedRequest.location?.formatted || 'Not specified'}</Typography>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Admin Contact Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">
                      {selectedRequest.requestedBy?.firstName} {selectedRequest.requestedBy?.lastName}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedRequest.requestedBy?.email}</Typography>
                  </Box>
                </Paper>
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                  Status Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Current Status</Typography>
                    <RequestStatusBadge status={selectedRequest.status} />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Request Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedRequest.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  {selectedRequest.reviewedAt && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Reviewed On</Typography>
                      <Typography variant="body1">
                        {new Date(selectedRequest.reviewedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {selectedRequest.rejectionReason && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {selectedRequest.rejectionReason}
                      </Alert>
                    </Box>
                  )}
                  {selectedRequest.notes && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary">Additional Notes</Typography>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {selectedRequest.notes}
                      </Alert>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              {selectedRequest.status === 'requested' && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleCloseDialogs();
                        handleOpenRejectDialog(selectedRequest);
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        handleCloseDialogs();
                        handleOpenMoreInfoDialog(selectedRequest);
                      }}
                    >
                      Request Info
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        handleCloseDialogs();
                        handleEnhancedApproveTenantRequest(selectedRequest);
                      }}
                    >
                      Approve
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleCloseDialogs} sx={{ minWidth: { xs: '100%', sm: 'auto' }, mb: { xs: 1, sm: 0 } }}>Close</Button>
          {selectedRequest?.status === 'requested' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  handleCloseDialogs();
                  handleOpenRejectDialog(selectedRequest);
                }}
                fullWidth={isMobile}
              >
                Reject
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={() => {
                  handleCloseDialogs();
                  handleOpenMoreInfoDialog(selectedRequest);
                }}
                fullWidth={isMobile}
              >
                Request Info
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleCloseDialogs();
                  handleEnhancedApproveTenantRequest(selectedRequest);
                }}
                fullWidth={isMobile}
              >
                Approve
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reject Tenant Request
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="body1" gutterBottom>
                You are about to reject the registration request for:
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {selectedRequest.name} ({selectedRequest.subdomain})
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Reason for Rejection"
                fullWidth
                multiline
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                helperText="Please provide a reason for the rejection. This will be sent to the requester."
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button 
            onClick={handleRejectTenantRequest} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request More Info Dialog */}
      <Dialog
        open={showMoreInfoDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request More Information
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="body1" gutterBottom>
                You are requesting more information for:
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {selectedRequest.name} ({selectedRequest.subdomain})
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Information Request Message"
                fullWidth
                multiline
                rows={4}
                value={moreInfoMessage}
                onChange={(e) => setMoreInfoMessage(e.target.value)}
                required
                helperText="Specify what additional information you need. This message will be sent to the requester."
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button 
            onClick={handleRequestMoreInfo} 
            color="info" 
            variant="contained"
            disabled={!moreInfoMessage}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Menu */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#3f51b5' }} />
          Complete Financial Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('revenue')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#4caf50' }} />
          Revenue Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('transactions')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#ff9800' }} />
          Transactions Only
        </MenuItem>
      </Menu>
      
      {/* Print Preview Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            System-Wide Financial Report
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
            <AllTenantsFinancialReport
              ref={financialReportRef}
              tenants={tenants}
              paymentStats={paymentStats}
              transactions={allTenantTransactions}
              reportType={printReportType}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Printing Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            Generating PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

export default SuperAdminDashboard;
